import { type FC, useCallback, useMemo } from 'react';
import { AxiosInstance } from 'axios';
import { useLibraryTranslation } from '../../../i18n';
import { QSelect, QSelectPlaceholder, QText } from '../../../QAtoms';

import { SearchResource } from '../../../types/domains/Search';
import { QBox } from '../../../QLayouts';
import { useQuery } from 'react-query';
import { QuickSearchQueryClause } from '../../Table/DataProviders';
import { domainOrResourceMapping } from '../../../QMolecules';
import { useFeatureFlag } from '../../../hooks';

import {
  ResourceInfoFilter,
  ResourceInfoFilterV2,
  ResourceInfoParser,
  ResourceInfoParserV2,
} from './QuickSearchFilterTypes';

/**
 * Adds or replaces a term clause in the given query array
 */
function upsertOrDeleteTermClauseByKey(
  queryArray: QuickSearchQueryClause[],
  key: string,
  value?: string | number | boolean,
): QuickSearchQueryClause[] {
  const updatedArray: QuickSearchQueryClause[] = [];

  let found = false;

  for (const clause of queryArray) {
    if (clause.term && clause.term.hasOwnProperty(key)) {
      found = true;
      if (value !== undefined) {
        updatedArray.push({ term: { [key]: value } });
      }
    } else {
      updatedArray.push(clause);
    }
  }

  if (!found && value !== undefined) {
    updatedArray.push({ term: { [key]: value } });
  }

  return updatedArray;
}

type QuickSearchFilterProps = {
  companyId: number;
  searchClient: AxiosInstance;
  setMustFilter: (filters: QuickSearchQueryClause[]) => void;
  mustFilter: QuickSearchQueryClause[] | undefined;
  setResources: (resources: SearchResource | SearchResource[]) => void;
  resources: SearchResource | SearchResource[];
  defaultResources: SearchResource | SearchResource[];
  setResourceFilterSelected: (selected: boolean) => void;
};

export const QuickSearchFilters: FC<QuickSearchFilterProps> = ({
  searchClient,
  companyId,
  setMustFilter,
  mustFilter,
  setResources,
  resources,
  defaultResources,
  setResourceFilterSelected,
}) => {
  const { t } = useLibraryTranslation();
  const useV2ResourceConfigEndpoint =
    useFeatureFlag()[
      'quicksearch-resource-filters-use-resource-config-endpoint-v2'
    ];

  const isResourceInfoResponseV2 = (
    item: ResourceInfoFilter | ResourceInfoFilterV2,
  ): item is ResourceInfoFilterV2 => {
    return useV2ResourceConfigEndpoint as boolean;
  };

  const { data } = useQuery(['quicksearch', resources], async () => {
    try {
      if (!resources) {
        return { filters: [] };
      }

      if (Array.isArray(resources)) {
        return { filters: [] };
      }

      const resourceConfigEndpoint = useV2ResourceConfigEndpoint
        ? `v2/companies/${companyId}/resources/${resources}/info`
        : `companies/${companyId}/resources/${resources}/info`;

      const response = await searchClient.get(resourceConfigEndpoint);

      const parsed = useV2ResourceConfigEndpoint
        ? ResourceInfoParserV2.parse(response.data)
        : ResourceInfoParser.parse(response.data);

      return parsed;
    } catch (error) {
      console.warn(
        `Failed to fetch resources from quicksearch companies/${companyId}/resources/${resources}/info,`,
        { error },
      );
      return { filters: [] };
    }
  });

  const handleChangeMustSelectItems = useCallback(
    (val, item) => {
      if (val) {
        /**
         * If a specific value selected, we make sure to upsert the value to the existing mustFilter.
         * If mustFilter is yet to be defined, we will use an emtpy array instead.
         */
        setMustFilter(
          upsertOrDeleteTermClauseByKey(mustFilter ?? [], item.term, val.value),
        );
        return;
      }

      /**
       * In this scenario, value is not defined and we can therefore conclude that the user
       * has cleared the select field. We delete the item in the mustFilter array by providing an undefined value
       */
      setMustFilter(
        upsertOrDeleteTermClauseByKey(mustFilter ?? [], item.term, undefined),
      );
    },
    [mustFilter],
  );

  const handleResourceFilterChange = useCallback(
    (val) => {
      if (val === null) {
        /**
         * In this scenario, the user has cleared the select input, this means that we need to fallback to
         * the default selected resources. Default selected resources are a fixed set of resources that
         * will be queried for.
         */
        setResources(defaultResources);
        setMustFilter([]);
        setResourceFilterSelected(false);
        return;
      }

      /**
       * When the user selects a new resource, we need to make sure to reset the must filters as well since
       * The must filters are dependent on the resource selected.
       */
      setResources(val.value as SearchResource);
      setMustFilter([]);
      setResourceFilterSelected(true);
    },
    [defaultResources],
  );

  const options = useMemo(() => {
    return (
      Array.isArray(defaultResources)
        ? defaultResources.map((value) => {
            return {
              label:
                // fallback needed in case the user provides an invalid value that is incorrectly type cast to the expected search domain type.
                domainOrResourceMapping[value]?.label ??
                value.replace(/-/g, ' '),
              value,
            };
          })
        : [
            {
              label:
                // fallback needed in case the user provides an invalid value that is incorrectly type cast to the expected search domain type.
                domainOrResourceMapping[defaultResources]?.label ??
                defaultResources.replace(/-/g, ' '),
              value: defaultResources,
            },
          ]
    ).sort((a, b) => a.label.localeCompare(b.label));
  }, [defaultResources]);

  /**
   * Helper function to generate a unique key and get selected value for each api generated filter
   * This helper function is needed to make sure each filter contains a unique key and we can populate
   * the its value property. Without, we cannot properly unselect a value.
   *
   * @param term the term that the user will a select a value for
   * @param mustFilter the mustFilter that is sent to the quicksearch api
   */
  const findSelectedValueInMustFilter = (
    term: string,
    mustFilter?: QuickSearchQueryClause[],
  ) => {
    if (!mustFilter) {
      return null;
    }
    const foundTermInMustFilter = mustFilter.find((query) => {
      if (query.term) {
        return Object.keys(query.term).includes(term);
      }
      return false;
    });

    return foundTermInMustFilter?.term?.[term]
      ? {
          value: foundTermInMustFilter.term[term],
          uniqueKey: `${term} - ${foundTermInMustFilter.term[term]}`,
        }
      : null;
  };

  return (
    <>
      <QBox maxW="223px" minW="223px">
        <QSelect
          size="sm"
          key={resources.toString()}
          options={options}
          onChange={handleResourceFilterChange}
          value={Array.isArray(resources) ? '' : resources}
          isClearable
        >
          <QSelectPlaceholder>
            <QText>{t('lookup.resourcePlaceholder')}</QText>
          </QSelectPlaceholder>
        </QSelect>
      </QBox>

      {data &&
        data.filters.map((item) => {
          const selectedValue = findSelectedValueInMustFilter(
            item.term,
            mustFilter,
          );

          if (item.values.length === 0) {
            return;
          }

          return (
            <QBox key={item.term} minW="223px" maxW="223px">
              <QSelect
                size="sm"
                key={selectedValue?.uniqueKey ?? null}
                options={
                  isResourceInfoResponseV2(item)
                    ? item.values
                    : item.values.map((value: string) => ({
                        label: value,
                        value,
                      }))
                }
                onChange={(val) => handleChangeMustSelectItems(val, item)}
                isClearable
                value={selectedValue?.value ?? null}
              >
                <QSelectPlaceholder>
                  <QText casing="capitalize">{item.displayName}...</QText>
                </QSelectPlaceholder>
              </QSelect>
            </QBox>
          );
        })}
    </>
  );
};
